"use client";

import { DetailDrawer, DetailDrawerSkeleton } from "@/components/ui/detail-drawer";
import { Text } from "@/components/ui/text";
import { ApiErrorPanel } from "@/components/ui/api-error-panel";
import { MomentumBadge } from "@/components/MomentumBadge";
import { DealMomentumBlockersPanel } from "@/components/DealMomentumBlockersList";
import { useDealMomentum } from "@/hooks/use-deal-momentum";
import { getApiErrorMessage } from "@/lib/api-error-message";
import { DEAL_MOMENTUM_DISCLAIMER, dealMomentumIndicatorLabel } from "@/lib/momentum-blocker";
import { hasDealMomentumTranscriptAvailable } from "@/lib/no-transcript";

interface DealMomentumDrawerProps {
  meetingId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DealMomentumDrawer({
  meetingId,
  open,
  onOpenChange,
}: DealMomentumDrawerProps) {
  const { data, isLoading, isError, error, refetch } = useDealMomentum({
    meetingId,
    enabled: open,
  });

  const blockers = data?.blockers ?? [];
  const hasDealMomentumData = hasDealMomentumTranscriptAvailable(
    data?.has_deal_momentum_data,
    data?.call_count,
  );
  const indicatorLabel = dealMomentumIndicatorLabel(blockers.length);

  return (
    <DetailDrawer
      open={open}
      onOpenChange={onOpenChange}
      title="What's blocking this deal"
      side="right"
    >
      {isLoading ? (
        <DetailDrawerSkeleton />
      ) : isError ? (
        <ApiErrorPanel
          message={getApiErrorMessage(error)}
          onTryAgain={() => refetch()}
        />
      ) : data ? (
        <div className="space-y-6">
          {hasDealMomentumData ? (
            <div className="flex flex-wrap items-center gap-3">
              <MomentumBadge
                label={data.momentum_label}
                direction={data.momentum_direction}
              />
              <Text as="span" size="sm" weight="normal" color="muted">
                {indicatorLabel}
              </Text>
            </div>
          ) : null}

          <DealMomentumBlockersPanel
            blockers={blockers}
            hasDealMomentumData={hasDealMomentumData}
            disclaimer={DEAL_MOMENTUM_DISCLAIMER}
          />
        </div>
      ) : (
        <Text as="p" size="sm" weight="normal" color="muted">
          No deal momentum data available.
        </Text>
      )}
    </DetailDrawer>
  );
}
